import { create } from "zustand";
import {
  api,
  type InsightOverview,
  type InsightsTimeseries,
  type InsightsRange,
  type OcrUsageSummary,
  type TranslationUsageSummary,
  INSIGHTS_METRICS,
} from "@/lib/api";

interface InsightsState {
  range: InsightsRange;
  overview: InsightOverview | null;
  timeseries: Partial<Record<string, InsightsTimeseries>>;
  /** O custo do OCR, lido do livro-razão — não dos snapshots. */
  ocr: OcrUsageSummary | null;
  /** O custo da tradução do Ler Texto (TR1), do livro-razão dela. */
  translation: TranslationUsageSummary | null;
  loading: boolean;
  collecting: boolean;
  error: string | null;
  setRange: (range: InsightsRange) => void;
  refresh: () => Promise<void>;
  collectNow: () => Promise<void>;
}

export const useInsightsStore = create<InsightsState>((set, get) => ({
  range: "24h",
  overview: null,
  timeseries: {},
  ocr: null,
  translation: null,
  loading: false,
  collecting: false,
  error: null,

  setRange: (range) => {
    set({ range });
    void get().refresh();
  },

  refresh: async () => {
    const { range } = get();
    set({ loading: true, error: null });
    try {
      const [overview, ocr, translation, ...timeseriesResults] = await Promise.all([
        api.getInsightsOverview(range),
        api.getInsightsOcr(range),
        // Suplementar: um backend anterior à TR1 responde 404 aqui, e a tela
        // segue sem a seção em vez de cair inteira.
        api.getInsightsTranslation(range).catch(() => null),
        ...INSIGHTS_METRICS.map((metric) => api.getInsightsTimeseries(metric, range)),
      ]);
      const timeseries: Partial<Record<string, InsightsTimeseries>> = {};
      INSIGHTS_METRICS.forEach((metric, i) => {
        timeseries[metric] = timeseriesResults[i];
      });
      set({ overview, ocr, translation, timeseries, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro desconhecido ao carregar dados.";
      set({ loading: false, error: message });
    }
  },

  collectNow: async () => {
    set({ collecting: true, error: null });
    try {
      await api.runInsightsSnapshot();
      await get().refresh();
      set({ collecting: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro desconhecido ao coletar snapshot.";
      set({ collecting: false, loading: false, error: message });
    }
  },
}));
